// Post-login step: pick the gate this session works at. One gate → picked automatically (unless
// the user came here to switch). No gate → told to ask the gate admin; a gate admin is sent on to
// set gates up.

import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight, DoorOpen, LogOut, Settings2 } from "lucide-react";

import { useAuth } from "@/lib/auth";
import type { GateInfo } from "@/lib/gate";
import { useMe } from "@/lib/me";
import { APP_NAME } from "@/lib/nav";
import { useSessionScope } from "@/lib/session-scope";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export default function SelectGate() {
  const { displayName, username, signOut } = useAuth();
  const { me, error, loading } = useMe();
  const { scope, setScope } = useSessionScope();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const switching = params.get("switch") === "1";

  function choose(gate: GateInfo) {
    if (!me) return;
    setScope({ gate, isAdmin: me.isAdmin });
    navigate(next.startsWith("/") && !next.startsWith("/select-gate") ? next : "/", { replace: true });
  }

  // A single gate needs no choice.
  useEffect(() => {
    if (me && me.gates.length === 1 && !switching) {
      setScope({ gate: me.gates[0], isAdmin: me.isAdmin });
      navigate(next.startsWith("/") && !next.startsWith("/select-gate") ? next : "/", { replace: true });
    }
  }, [me, switching, next, navigate, setScope]);

  return (
    <div className="flex min-h-svh items-start justify-center bg-muted/40 px-4 pt-[12vh] pb-8">
      <div className="flex w-full max-w-md flex-col gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
            <DoorOpen className="size-6" />
          </span>
          <h1 className="text-xl font-bold">{APP_NAME}</h1>
          <p className="text-sm text-muted-foreground">
            Hare Krishna{displayName || username ? `, ${displayName || username}` : ""}. Which gate are you at?
          </p>
        </div>

        <Card>
          <CardContent className="flex flex-col gap-2">
            {error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : loading || !me ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : me.gates.length === 0 ? (
              <div className="flex flex-col gap-3 text-sm">
                <p>You are not assigned to any gate yet.</p>
                {me.isAdmin ? (
                  <Button nativeButton={false} render={<Link to="/admin" />}>
                    <Settings2 /> Set up gates and guards
                  </Button>
                ) : (
                  <p className="text-muted-foreground">Ask your location&apos;s gate admin to add you to a gate.</p>
                )}
              </div>
            ) : (
              me.gates.map((g) => (
                <Button
                  key={g.id}
                  variant="outline"
                  onClick={() => choose(g)}
                  className="h-auto min-h-16 w-full justify-start gap-3 rounded-2xl px-4 py-3 text-left font-normal whitespace-normal data-[current=true]:border-primary data-[current=true]:bg-primary/5"
                  data-current={scope?.gate.id === g.id}
                >
                  <DoorOpen className="size-6 shrink-0 text-primary" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-semibold">{g.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[g.operationUnit?.name, g.description].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <ChevronRight className="size-4 text-muted-foreground" />
                </Button>
              ))
            )}
            {me?.isAdmin && me.gates.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="self-start"
                nativeButton={false}
                render={<Link to="/admin" />}
              >
                <Settings2 /> Manage gates and guards
              </Button>
            )}
          </CardContent>
        </Card>

        <Button variant="ghost" className="self-center text-muted-foreground" onClick={() => signOut()}>
          <LogOut /> Sign out
        </Button>
      </div>
    </div>
  );
}
